import { useMemo } from "react";
import moment from "moment";

import { buildDayRows, buildPeriodDatesSet } from "../../../../utils/nutrition-progress";
import { buildPrExerciseKeysByDay } from "../../../../utils/workout-stats";

// The normalized per-day rows every view reads, plus the Log-only PR
// lookup — all memoized here so switching views (or opening the Customise
// Table modal) doesn't recompute them. Mirrors useNutritionProgressFilters.js's
// pattern: pull derived-data plumbing out of NutritionProgress.js into its
// own hook.
export const useNutritionProgressData = ({
  dailyStats,
  nutritionPlans,
  activeExercises,
  dateFrom,
  dateTo,
  femaleCycleLogs,
  femaleCycleSettings,
  dayPersonalRecords,
}) => {
  // Builds the period dates set for the nutrition progress page
  const periodDatesSet = useMemo(
    () => buildPeriodDatesSet(femaleCycleLogs, femaleCycleSettings),
    [femaleCycleLogs, femaleCycleSettings]
  );

  // Scoped exactly to the reporting period: every rollup/average across all
  // views must stay pinned to this range.
  const dayRows = useMemo(
    () =>
      buildDayRows({
        dailyStats,
        nutritionPlans,
        activeExercises,
        dateFrom,
        dateTo,
        periodDatesSet,
      }),
    [dailyStats, nutritionPlans, activeExercises, dateFrom, dateTo, periodDatesSet]
  );

  // Calendar + Log: both render ISO week grids that pad the first/last row
  // with days from outside the reporting period (whichever week
  // dateFrom/dateTo fall in). Those padding days still have real stats worth
  // showing, so this widens the same adapter to the full ISO week span —
  // kept separate from dayRows so no rollup/average anywhere else picks up
  // days outside the period.
  const weekGridDayRows = useMemo(
    () =>
      dateFrom && dateTo
        ? buildDayRows({
            dailyStats,
            nutritionPlans,
            activeExercises,
            dateFrom: moment(dateFrom).startOf("isoWeek").toDate(),
            dateTo: moment(dateTo).endOf("isoWeek").toDate(),
            periodDatesSet,
          })
        : dayRows,
    [dailyStats, nutritionPlans, activeExercises, dateFrom, dateTo, periodDatesSet, dayRows]
  );

  // Log-only: PR badge on LogTrainingCard. Computed once here (not per-cell)
  // since dayPersonalRecords covers the whole loaded window regardless of
  // which week is currently visible.
  const prExerciseKeysByDay = useMemo(
    () => buildPrExerciseKeysByDay(dayPersonalRecords),
    [dayPersonalRecords]
  );
  const repMaxPR = dayPersonalRecords?.repMaxPR;

  return { dayRows, weekGridDayRows, prExerciseKeysByDay, repMaxPR };
};
